import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable ,  BehaviorSubject ,  Subscription } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';

import { FitmentsSearchModel } from '../fitments-search.model';
import { ActiveFilterModel } from '../../search/active-filters/active-filter.model';
import { TableComponent } from '../../table/table/table.component';
import { FitmentsAdvancedSearchComponent } from '../fitments-advanced-search/fitments-advanced-search.component';

import { BrandService } from '../../brand/brand.service';
import { FitmentsService } from '../../fitments/fitments.service';
import {debounceTime, distinctUntilChanged, first, takeWhile} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';


@Component({
  moduleId: module.id,
  selector: 'fitments-table',
  templateUrl: 'fitments-table.component.html',
  styleUrls: ['fitments-table.component.scss'],
})
export class FitmentsTableComponent extends TableComponent implements OnInit, OnDestroy {
  @Input() declare data: any;

  @Input() headers: Array<string> = ['year', 'make', 'model', 'sub_model'];

  @Input() itemId: any;
  @Input() productId: any;

  @Input() emptyStateText: string = 'You have not defined Fitments for this item.';

  @Input() tableStriped: boolean = true;

  @Input() tableNarrow: boolean = false;

  @Input() perPageDropdown: boolean = false;

  @Input() dropup: boolean = false;

  @Input() itemsPerPage: number;

  @Input() hasSearch: boolean = false;

  @Input() advancedSearchStatus: string = 'working';

  @Input() ghostValue: string;

  @Output() pageChanged: EventEmitter<any> = new EventEmitter();

  @Output() itemsPerPageChanged: EventEmitter<any> = new EventEmitter();

  @Output() dataFetched: EventEmitter<any> = new EventEmitter();

  @ViewChild(FitmentsAdvancedSearchComponent, {static: false})
  advancedSearch: FitmentsAdvancedSearchComponent | void;

  fitmentsSearchModelObservable: Observable<FitmentsSearchModel>;
  fitments: any[] = [];
  attributes: any[];
  engineBase: any[];
  totalRecords: number = 0;
  searchControl: UntypedFormControl;
  showAdvancedSearch: boolean = false;
  excludedFields: Array<string> = [
    'year',
    'make',
    'model',
    'sub_model',
    'assignment_values.notes',
  ];

  private _fitmentsSearchModel: BehaviorSubject<FitmentsSearchModel>;
  private fitmentsSearchModelSub: Subscription;
  private fitmentsSearchModel: FitmentsSearchModel;
  private isActive: boolean = true;

  get filters(): Array<ActiveFilterModel> {
    return this.fitmentsSearchModel.filters;
  }

  get brandId(): any {
    return this.brandService.getBrand();
  }

  constructor(
    private brandService: BrandService,
    private fitmentsService: FitmentsService,
    private ref: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.fitmentsSearchModel = new FitmentsSearchModel();
    if (this.itemsPerPage) { this.fitmentsSearchModel.itemsPerPage = this.itemsPerPage; }
    this.fitmentsSearchModelSub = this.subscribeFitmentsSearchModel();
    this.setBlocksData();
    this.getFitments(this.fitmentsSearchModel);
    this.getLookups();
    // if (this.hasSearch) this.subscribeSearchTerm();
  }

  ngOnDestroy() {
    if (this.fitmentsSearchModelSub) { this.fitmentsSearchModelSub.unsubscribe(); }
    this.fitments = [];
    this.attributes = [];
    this.engineBase = [];
    this.isActive = false;
  }

  toggleAdvancedSearch() {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  runSearch() {
    this._fitmentsSearchModel.next(this.fitmentsSearchModel);
  }

  onPageChange(page: any) {
    this.fitmentsSearchModel.currentPage = page;
    this.pageChanged.emit(page);
    _.defer(() => {
      this.runSearch();
    });
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.fitmentsSearchModel.itemsPerPage = itemsPerPage;
    this.itemsPerPageChanged.emit(itemsPerPage);
    _.defer(() => {
      this.runSearch();
    });
  }

  onCancelClick() {
    this.showAdvancedSearch = false;
  }

  onSearchClick() {
    this.showAdvancedSearch = false;
    if (!this.advancedSearch) {
      return;
    }
    const advSearchActiveFilters = _.filter(this.advancedSearch.activeFilters, (filter: ActiveFilterModel) => {
      return !_.get(filter, 'data.multiple');
    });
    const vehicleFilters = this.advancedSearch.vehicleFilters;
    const vcdbFilters = this.advancedSearch.selectedVcdbFilters;
    this.fitmentsSearchModel.filters = _.concat(
      advSearchActiveFilters,
      vehicleFilters,
      vcdbFilters,
    );
    this.advancedSearch.resetAllData();
    this.runSearch();
  }

  onFiltersClearClick() {
    _.remove(this.fitmentsSearchModel.filters);
    if (this.advancedSearch instanceof FitmentsAdvancedSearchComponent) {
      this.advancedSearch.clearFilters();
    }
    this.runSearch();
  }

  onFilterRemove($event: any) {
    if (this.advancedSearch instanceof FitmentsAdvancedSearchComponent) {
      this.advancedSearch.removeFilter($event);
    }
    this.runSearch();
  }

  onSortChange(sort: any) {
    _.assign(this.fitmentsSearchModel, sort);
    this._fitmentsSearchModel.next(this.fitmentsSearchModel);
  }

  getFieldValue(fitment: any, item: any) {
    if (item.lookup) {
      const option = _.get(fitment, item.item_name, '');
      return _.get(_.find(item.lookup, { option }), 'title', '');
    } else {
      return super.getFieldValue(fitment, item);
    }
  }

  private setBlocksData() {
    const groupedData: any = _.groupBy(
      _.filter(this.data, (item: any) => !_.includes(this.excludedFields, item.item_name)),
      (item: any) => {
        return item.item_name.indexOf('engine') >= 0;
    });
    this.attributes = groupedData['false'];
    this.engineBase = groupedData['true'];
  }

  private getFitments(searchModel: FitmentsSearchModel) {
    this.fitmentsService.fetchProductFitments(this.brandId, this.productId, searchModel)
      .pipe(takeWhile(() => this.isActive))
      .subscribe((data) => {
        _.each(data.data, (fitment: any) => {
          fitment.hasEngineData = _.some(this.engineBase, (item: any) => {
            return !!this.getFieldValue(fitment, item);
          });
        });
        this.fitments = data.data;
        this.totalRecords = data.total;
        _.defer(() => this.dataFetched.emit(data));
        this.ref.markForCheck();
    });
  }

  private subscribeFitmentsSearchModel(): Subscription {
    this._fitmentsSearchModel = <BehaviorSubject<FitmentsSearchModel>>new BehaviorSubject(this.fitmentsSearchModel);
    this.fitmentsSearchModelObservable = this._fitmentsSearchModel.asObservable();

    return this.fitmentsSearchModelObservable
      .pipe(debounceTime(500))
      .subscribe((fitmentsSearchModel) => {
        this.getFitments(fitmentsSearchModel);
      });
  }

  private subscribeSearchTerm() {
    this.searchControl = new UntypedFormControl();
    this.searchControl.valueChanges
    .pipe(
        distinctUntilChanged(),
        takeWhile(() => this.isActive))
    .subscribe(
      (event) => {
        this.fitmentsSearchModel.search = event;
        this.fitmentsSearchModel.currentPage = 1;
        this.runSearch();
      });
  }

  private getLookups() {
    forkJoin([
      this.fitmentsService.getPositions(this.brandId, this.productId),
      this.fitmentsService.fetchYmmFilters('region')
    ])
    .pipe(
        first(),
        takeWhile(() => this.isActive)
    )
    .subscribe((result: any) => {
      this.setPositions(result[0]);
      this.setRegions(result[1]);
      _.defer(() => this.ref.markForCheck());
    });
  }

  private setPositions(data: any): void {
    const position: any = _.find(this.data, ['item_name', 'assignment_values.position_id']);
    _.set(position, 'lookup', data);
  }

  private setRegions(data: any): void {
    const region: any = _.find(this.data, ['item_name', 'assignment_values.region_id']);
    _.set(region, 'lookup', data);
  }
}
